import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { groupSizesByRegion } from '../../../util/sizes';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// Search filter options use `option` as the key field (rather than `key`).
const getSizeOptionKey = opt => opt.option;

/**
 * Determines which group a specific option belongs to
 */
const getGroupForOption = (optionKey, groupedSizes) => {
  for (const [groupName, groupOptions] of Object.entries(groupedSizes)) {
    if (groupOptions.some(opt => opt.option === optionKey)) {
      return groupName;
    }
  }
  return null;
};

/**
 * Renders a size label, splitting out bracketed content onto a second line
 */
const renderSizeLabel = label => {
  const bracketMatch = label.match(/^(.+?)(\([^)]+\))$/);
  if (bracketMatch) {
    return (
      <>
        <span className={css.sizeButtonMainText}>{bracketMatch[1].trim()}</span>
        <span className={css.sizeButtonSubText}>{bracketMatch[2]}</span>
      </>
    );
  }
  return label;
};

/**
 * Size button grid component for filter
 */
const SizeButtonGrid = props => {
  const { name, options, selectedValues, onToggle } = props;

  const groupedSizes = groupSizesByRegion(options, getSizeOptionKey);
  const hasMultipleGroups = Object.keys(groupedSizes).length > 1;

  return (
    <div className={css.sizeGroupsContainer}>
      {Object.entries(groupedSizes).map(([groupName, groupOptions]) => (
        <div key={groupName} className={css.sizeGroup}>
          {hasMultipleGroups && (
            <div className={css.sizeGroupLabel}>{groupName}</div>
          )}
          <div className={css.sizeGrid}>
            {groupOptions.map(opt => {
              const isSelected = selectedValues.includes(opt.option);
              return (
                <button
                  key={opt.option}
                  type="button"
                  className={classNames(css.sizeButton, {
                    [css.sizeButtonSelected]: isSelected,
                  })}
                  onClick={() => onToggle(opt.option)}
                >
                  {renderSizeLabel(opt.label)}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options } = props;
  return (
    <fieldset className={className}>
      <ul className={css.list}>
        {options.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox id={fieldId} name={name} label={label} value={option} />
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

/**
 * SelectMultipleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {node} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {Array<Object>} props.options - The options
 * @param {SCHEMA_TYPE_ENUM | SCHEMA_TYPE_MULTI_ENUM} props.schemaType - The schema type
 * @param {'has_all' | 'has_any'} props.searchMode - The search mode
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @returns {JSX.Element}
 */
/**
 * Wrapper component that uses Field to render SizeButtonGrid with form state
 */
const SizeButtonGridField = props => {
  const { name, options, groupedSizes } = props;

  return (
    <Field name={name}>
      {({ input }) => {
        const selectedValues = Array.isArray(input.value) ? input.value : [];

        const handleToggle = optionKey => {
          const isCurrentlySelected = selectedValues.includes(optionKey);

          if (isCurrentlySelected) {
            input.onChange(selectedValues.filter(v => v !== optionKey));
          } else {
            // Determine which group this option belongs to
            const clickedGroup = getGroupForOption(optionKey, groupedSizes);

            if (clickedGroup) {
              // Get all option keys in the same group
              const groupOptionKeys = groupedSizes[clickedGroup].map(opt => opt.option);
              // Remove any existing selections from this group, then add the new selection
              const valuesWithoutCurrentGroup = selectedValues.filter(
                v => !groupOptionKeys.includes(v)
              );
              input.onChange([...valuesWithoutCurrentGroup, optionKey]);
            } else {
              input.onChange([...selectedValues, optionKey]);
            }
          }
        };

        return (
          <SizeButtonGrid
            name={name}
            options={options}
            selectedValues={selectedValues}
            onToggle={handleToggle}
          />
        );
      }}
    </Field>
  );
};

const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
        { id: 'SelectMultipleFilter.labelSelected' },
        { labelText: label, count: selectedOptions.length }
      )
    : label;

  const labelSelectionForPlain = (() => {
    if (!hasInitialValues || selectedOptions.length === 0) return 'Any';
    if (selectedOptions.length === 1) {
      const selected = selectedOptions[0];
      const match = options.find(o => o.option === selected);
      return match ? match.label : 'Any';
    }
    return 'Multi';
  })();

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  // Detect if this is a size field
  const isSizeField = name.toLowerCase().includes('size');
  const groupedSizes = isSizeField ? groupSizesByRegion(options, getSizeOptionKey) : null;

  // Render content based on whether it's a size field
  const renderFilterContent = () => {
    if (isSizeField) {
      return (
        <SizeButtonGridField
          name={name}
          options={options}
          groupedSizes={groupedSizes}
        />
      );
    }
    return (
      <GroupOfFieldCheckboxes
        className={showAsPopup ? css.fieldGroup : css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
      />
    );
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      {renderFilterContent()}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      {renderFilterContent()}
    </FilterPlain>
  );
};

export default SelectMultipleFilter;
